"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  LifeBuoy, 
  Lightbulb, 
  BookOpen, 
  Share2, 
  ArrowRight,
  Calendar
} from "lucide-react";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { TextGradient } from "@/components/ui/text-gradient";
import { cn } from "@/lib/utils";

interface InitiativeCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  label?: string;
  index: number;
  isInView: boolean;
}

function InitiativeCard({ icon, title, description, features, label, index, isInView }: InitiativeCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      className={cn(
        "bg-white rounded-xl p-6 border border-gray-100 shadow-sm",
        "hover:shadow-md transition-all duration-300"
      )}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="h-12 w-12 rounded-xl bg-blue-light flex items-center justify-center">
          <div className="text-blue-medium">
            {icon}
          </div>
        </div>
        
        {label && (
          <Badge variant="blue-light" size="sm">{label}</Badge>
        )}
      </div>

      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-muted-foreground mb-5">{description}</p>
      
      <div className="space-y-2 mb-6">
        {features.map((feature, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="h-5 w-5 text-blue-medium mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm">{feature}</span>
          </div>
        ))}
      </div>
      
      <Button variant="ghost" className="group text-blue-medium hover:text-blue-dark w-full justify-between">
        Learn More 
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Button>
    </motion.div>
  );
}

export function InitiativesSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const { ref: eventsRef, inView: eventsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const initiatives = [
    {
      icon: <LifeBuoy className="h-6 w-6" />,
      title: "Blue Brick Rescue",
      description: "Recovering and restoring forgotten blue Lego pieces to give them new life in creative builds.",
      features: [
        "Brick donation collection centers",
        "Cleaning and restoration stations",
        "Redistribution to schools and clubs"
      ],
      label: "Most Popular"
    },
    {
      icon: <Lightbulb className="h-6 w-6" />,
      title: "Build Blue Challenge",
      description: "Monthly building competitions showcasing the creative potential of blue Lego bricks.",
      features: [
        "Global online submissions",
        "Expert judging panel",
        "Exhibition of winning designs"
      ]
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: "Blue Education Program",
      description: "Workshops and materials teaching the artistic and engineering value of blue bricks.",
      features: [
        "School outreach program",
        "Blue brick building techniques",
        "Color theory in Lego design"
      ]
    },
    {
      icon: <Share2 className="h-6 w-6" />,
      title: "Advocacy Campaign",
      description: "Raising awareness about blue brick equality through media and partnerships.",
      features: [
        "Corporate partnership program",
        "Social media campaigns",
        "Blue brick representation research"
      ]
    }
  ];

  const upcomingEvents = [
    {
      date: "Oct 15",
      title: "Blue Brick Building Workshop",
      location: "Chicago, IL",
      type: "Workshop"
    },
    {
      date: "Nov 5",
      title: "National Blue Lego Conference",
      location: "Virtual Event",
      type: "Conference"
    },
    {
      date: "Dec 10",
      title: "Holiday Blue Brick Drive",
      location: "Multiple Locations",
      type: "Fundraiser"
    }
  ];

  return (
    <SectionContainer id="initiatives" className="bg-white">
      <SectionHeading
        title={
          <>
            Our <TextGradient text="Initiatives" />
          </>
        }
        subtitle="Through these key programs, we're working to elevate the status of blue Lego bricks and ensure they receive equal opportunities in the building community."
      />

      <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {initiatives.map((initiative, i) => (
          <InitiativeCard
            key={i}
            index={i}
            isInView={inView}
            {...initiative}
          />
        ))}
      </div>

      {/* Upcoming Events */}
      <div 
        ref={eventsRef} 
        className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h3 className="text-2xl font-heading font-bold">Upcoming Events</h3>
            <p className="text-muted-foreground">Join us at these upcoming Blue Legos Matter events</p>
          </div>
          <Button variant="blue-outline" className="mt-4 md:mt-0">
            View All Events
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {upcomingEvents.map((event, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={eventsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <Card>
                <CardContent className="p-0">
                  <div className="flex border-b border-gray-100">
                    <div className="flex-shrink-0 bg-blue-light p-4 flex flex-col items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-medium mb-1" />
                      <span className="font-bold text-blue-dark text-lg leading-none">{event.date}</span>
                    </div>
                    <div className="p-4 flex-grow">
                      <h4 className="font-medium mb-1">{event.title}</h4>
                      <p className="text-sm text-muted-foreground">{event.location}</p>
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <Badge variant="blue-light">{event.type}</Badge>
                    <Button variant="ghost" size="sm" className="text-blue-medium p-0 hover:text-blue-dark">
                      Register <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}